import { useCallback, useRef } from 'react';
import { useMemoryRetrieval } from './useMemoryRetrieval';
import { useMemoryDB } from './useMemoryDB';

export interface WeavingContext {
  userMessage: string;
  messageCount: number;
  isNewDay: boolean;
  lastMessageTime?: number;
}

// 最近使った記憶IDを管理（同じ記憶の繰り返しを防ぐ）
const recentlyUsedMemories = new Set<string>();
const MAX_RECENT_USED = 5;

function markMemoryUsed(content: string) {
  recentlyUsedMemories.add(content);
  if (recentlyUsedMemories.size > MAX_RECENT_USED) {
    const first = recentlyUsedMemories.values().next().value;
    if (first !== undefined) recentlyUsedMemories.delete(first);
  }
}

function isMemoryRecentlyUsed(content: string): boolean {
  return recentlyUsedMemories.has(content);
}

// 記憶を会話に溶け込ませる接続フレーズ
function getWeaveConnector(style: 'soft' | 'natural' | 'followup'): string {
  if (style === 'soft') {
    const list = [
      'そういえば、',
      'ふと思い出したんですけど、',
      'ちょっと気になってたんですが、',
      'あ、そうだ。',
    ];
    return list[Math.floor(Math.random() * list.length)];
  }
  if (style === 'followup') {
    const list = [
      'ところで、',
      'そういえばずっと気になってたんですが、',
      'あの話、その後どうなりました？',
    ];
    return list[Math.floor(Math.random() * list.length)];
  }
  // natural
  const list = [
    '',
    'そうそう、',
    'あ、',
  ];
  return list[Math.floor(Math.random() * list.length)];
}

export function useMemoryWeaving() {
  const { retrieveRelevantMemories, getYesterdayMemories, getRandomMemoryPhrase, formatMemoryAsConversation, detectTopicTag } = useMemoryRetrieval();
  const { getMemories } = useMemoryDB();

  // 記憶を会話に織り込むべきタイミングか判定
  const shouldWeaveMemory = useCallback((context: WeavingContext): boolean => {
    // 新しい日の最初のメッセージ（翌日再訪時）
    if (context.isNewDay) {
      return Math.random() < 0.75;
    }

    // 4時間以上空いた後の再訪
    if (context.lastMessageTime) {
      const hoursSince = (Date.now() - context.lastMessageTime) / (1000 * 60 * 60);
      if (hoursSince >= 4) {
        return Math.random() < 0.55;
      }
    }

    // 通常の会話中（4〜6メッセージごと）
    if (context.messageCount > 0 && context.messageCount % 4 === 0) {
      return Math.random() < 0.35;
    }
    if (context.messageCount > 0 && context.messageCount % 6 === 0) {
      return Math.random() < 0.45;
    }

    return false;
  }, []);

  // 記憶を織り込んだ応答を生成（自然な会話の流れを維持）
  const weaveMemoryIntoResponse = useCallback(async (
    baseResponse: string,
    context: WeavingContext
  ): Promise<string> => {

    // ① 翌日再訪：昨日の話をフォローアップ
    if (context.isNewDay) {
      const yesterdayMemories = await getYesterdayMemories();
      const unused = yesterdayMemories.filter((m) => !isMemoryRecentlyUsed(m.content));
      if (unused.length > 0) {
        const memory = unused[0];
        markMemoryUsed(memory.content);
        const phrase = formatMemoryAsConversation(memory, 'followup');
        // 返答の後ろに自然につなげる
        return `${baseResponse}\n\n${getWeaveConnector('followup')}${phrase}`;
      }
    }

    // ② ユーザーメッセージのトピックに関連する記憶を検索
    const relevantMemories = await retrieveRelevantMemories(context.userMessage, 3);
    const unusedRelevant = relevantMemories.filter((m) => !isMemoryRecentlyUsed(m.content));

    if (unusedRelevant.length > 0) {
      const memory = unusedRelevant[0];
      markMemoryUsed(memory.content);

      // 関連度が高い場合は会話の前半に、低い場合は後半に織り込む
      if (memory.relevance >= 3) {
        // 高関連度：返答の中に自然に溶け込ませる
        const phrase = formatMemoryAsConversation(memory, 'casual');
        return `${baseResponse}\n${getWeaveConnector('soft')}${phrase}`;
      } else {
        // 中関連度：返答の後ろにさりげなく追加
        const phrase = formatMemoryAsConversation(memory, 'recall');
        return `${baseResponse}\n\n${getWeaveConnector('soft')}${phrase}`;
      }
    }

    // ③ ランダムな記憶をさりげなく織り込む（低頻度）
    if (Math.random() < 0.25) {
      const randomPhrase = await getRandomMemoryPhrase();
      if (randomPhrase) {
        return `${baseResponse}\n\n${getWeaveConnector('soft')}${randomPhrase}`;
      }
    }

    return baseResponse;
  }, [retrieveRelevantMemories, getYesterdayMemories, getRandomMemoryPhrase, formatMemoryAsConversation]);

  // 記憶の統計情報を取得
  const getMemoryStats = useCallback(async () => {
    const memories = await getMemories();
    const now = Date.now();

    const stats = {
      total: memories.length,
      today: memories.filter((m) => now - m.timestamp < 24 * 60 * 60 * 1000).length,
      thisWeek: memories.filter((m) => now - m.timestamp < 7 * 24 * 60 * 60 * 1000).length,
      byType: {
        profile: memories.filter((m) => m.type === 'profile').length,
        event: memories.filter((m) => m.type === 'event').length,
        relation: memories.filter((m) => m.type === 'relation').length,
      },
    };

    return stats;
  }, [getMemories]);

  // 記憶が十分にあるか確認
  const hasEnoughMemories = useCallback(async (): Promise<boolean> => {
    const stats = await getMemoryStats();
    return stats.total >= 2;
  }, [getMemoryStats]);

  return {
    shouldWeaveMemory,
    weaveMemoryIntoResponse,
    getMemoryStats,
    hasEnoughMemories,
  };
}
